import React, { useRef, useCallback, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  useConversation,
  useAskQuestion,
  useConversationSocket,
  useOnboarding,
  useDismissOnboarding,
} from "./hooks/network";
import ConversationView from "./conversation-view";
import { MessageInput } from "./message-input";
import { OnboardingCard } from "./onboarding-card";
import { SuggestionsCard } from "./suggestions-card";

let queryClient: QueryClient;

function getQueryClient() {
  if (!queryClient) {
    queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: 0,
          staleTime: 5 * 60 * 1000,
        },
      },
    });
  }
  return queryClient;
}

interface Props {
  conversationId: number;
}

function AiChat({ conversationId }: Props) {
  const { data, isLoading, error } = useConversation(conversationId);
  const { data: onboardingData, isLoading: onboardingLoading } =
    useOnboarding(conversationId);
  const askMutation = useAskQuestion();
  const dismissMutation = useDismissOnboarding();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useConversationSocket(conversationId);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "auto",
    });
  }, []);

  // Scroll to bottom on mount and when messages change (no animation)
  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  useEffect(() => {
    if (data?.conversation_messages.length) {
      scrollToBottom();
    }
  }, [data?.conversation_messages.length, scrollToBottom]);

  const handleSendMessage = (message: string) => {
    askMutation.mutate({ conversationId, question: message });
  };

  const handleDismissOnboarding = () => {
    dismissMutation.mutate(conversationId);
  };

  if (error) return <div>Error: {String(error)}</div>;
  if (isLoading || !data) return <div>Loading...</div>;

  return (
    <div className="flex flex-col h-full bg-slate-50 min-h-0 relative">
      <div className="w-full flex flex-col border-b border-slate-300 h-[60px] bg-white z-[2]">
        <div className="pageInset w-full">
          <h1 className="text-xl font-bold my-3 flex-shrink-0">
            {data.title || "Nouvelle conversation"}
          </h1>
        </div>
      </div>
      <div className="z-[1] relative flex flex-col h-full flex-shrink min-h-0 absolute w-screen">
        <div className="w-full h-full overflow-y-scroll min-h-0">
          <div className="max-w-4xl mx-auto p-6 pb-[134px] flex-shrink">
            {!onboardingLoading && onboardingData?.onboarding.show && (
              <div className="pb-6">
                <OnboardingCard
                  message={onboardingData.onboarding.message || ""}
                  onDismiss={handleDismissOnboarding}
                  dismissing={dismissMutation.isPending}
                />
              </div>
            )}

            {!onboardingLoading &&
              onboardingData?.suggestions &&
              onboardingData.suggestions.length > 0 && (
                <div className="pb-6">
                  <SuggestionsCard
                    suggestions={onboardingData.suggestions}
                    onSelect={(question) =>
                      askMutation.mutate({ conversationId, question })
                    }
                    disabled={askMutation.isPending}
                  />
                </div>
              )}

            <ConversationView
              conversation={data}
            />
            <div ref={messagesEndRef} />
          </div>

          <div className="absolute bottom-0 w-full flex-shrink-0 px-6 h-[110px]">
            <div className="max-w-4xl mx-auto px-6">
              <div className="-mx-0.5">
                <MessageInput
                  conversationId={conversationId}
                  onSend={handleSendMessage}
                  disabled={askMutation.isPending}
                />
              </div>
            </div>
            <div className="h-4 bg-slate-50"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AiChatApp({ conversationId }: Props) {
  return (
    <QueryClientProvider client={getQueryClient()}>
      <AiChat
        conversationId={conversationId}
      />
    </QueryClientProvider>
  );
}

// For testing
export function resetQueryClient() {
  queryClient = null as any;
}
